import { motion } from "framer-motion";
import { Outlet, useLocation, useNavigate, useParams, UNSAFE_NavigationContext as NavigationContext, To } from "react-router-dom";
import { useEffect, useState, useContext, useRef } from "react";
import clsx from "clsx";

import { agentActions, Agent } from "../redux/reducers/agentSlice";
import { sessionActions } from "../redux/reducers/sessionSlice";
import { AgentDebugger } from "../components/agents/AgentDebugger";
import { ReleaseDebugger } from "../components/agents/ReleaseDebugger";
import { agentEnvironmentActions } from "../redux/reducers/agentEnvironmentSlice";
import { agentFunctionActions } from "../redux/reducers/agentFunctionSlice";
import { Alert, AlertDescription, AlertTitle, Button, DropdownField, InputField, Spinner, SwitchField, Text, View, MenuField } from "../components/base";
import { CreateAgentEnvironmentDialog } from "../components/agents/CreateAgentEnvironmentDialog";
import { Icon } from "../components/base/Icon";
import { Tabs } from "../components/base/Tabs";
import { useAppDispatch, useAppSelector } from "../redux";
import { useKeyboardShortcut } from "../hooks/ussKeyboardShortcut";
import { useSearchParam } from "../hooks/useSearchParam";
import { getGradientForName } from "../utils/gradients";
import { Dialog } from "../components/base/Dialog";
import { featureActions } from "../redux/reducers/featureSlice";
import { useAuth } from "../hooks/useAuth";
import { useEffectiveOrganization } from "../hooks/useEffectiveOrganization";
import { AgentEnvironmentDropdownField } from "../components/AgentEnvironmentDropdownField";
import { AgentSettingsModal } from "../components/agents/AgentSettingsModal";
import { capitalize, formatPhoneNumber } from "../utils/strings";
import { fetchTestNotifications } from "../redux/reducers/testNotificationsSlice";
import { agentDeploymentActions } from "../redux/reducers/agentDeploymentSlice";
import { IconButton } from "../components/base/IconButton";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useApiRequest } from "../hooks/useApiRequest";
import { releaseActions } from "../redux/reducers/releaseSlice";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.5 },
  },
};

const DEPLOY_LOADING_MESSAGES = [
  "Warming up the servers… they’re a little sleepy.",
  "Polishing the bits ✨",
  "Teaching hamsters to run faster (they asked for snacks).",
  "This is where the magic happens. Please hold.",
  "Aligning rockets and double-checking seatbelts.",
];

// Custom hook to block navigation
function useBlocker(blocker: (nextPath: To) => boolean, when = true) {
  const { navigator } = useContext(NavigationContext);
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  useEffect(() => {
    if (!when) return;
    const push = navigator.push;
    navigator.push = (...args: [any]) => {
      if (blockerRef.current(...args)) {
        // Block navigation
        return;
      }
      push(...args);
    };
    return () => {
      navigator.push = push;
    };
  }, [when, navigator]);
}

export const AgentDetailLayout = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { agentId } = useParams<{ agentId?: string }>();
  const { isAdmin, user } = useAuth();
  const hasReleases = useAppSelector((state) => Object.values(state.release.entities).some((release) => release.agentId === agentId));
  const uiVersion: "v1" | "v2" = hasReleases ? "v2" : "v1";
  const debuggerParam = useSearchParam("debugger");
  const [isDebuggerOpen, setIsDebuggerOpen] = useState(debuggerParam === "open");

  // Open debugger when navigating to ?debugger=open (e.g. from onboarding drawer)
  useEffect(() => {
    if (debuggerParam === "open") setIsDebuggerOpen(true);
  }, [debuggerParam]);
  const agent = useAppSelector((state) => (agentId ? state.agent.entities[agentId] : null));
  const isLoadingAgent = useAppSelector((state) => state.agent.isLoading);
  const agentEnvironments = useAppSelector((state) => Object.values(state.agentEnvironment.entities).filter((env) => env.agentId === agentId));
  const agentFunctions = useAppSelector((state) => Object.values(state.agentFunction.entities).filter((env) => env.agentId === agentId));
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeployDialog, setShowDeployDialog] = useState(false);
  const [showAgentSettingsModal, setShowAgentSettingsModal] = useState(false);
  const [deployEnvironmentId, setDeployEnvironmentId] = useState<string>("");
  const [deploymentIsDedicated, setDeploymentIsDedicated] = useState<boolean>(false);
  const isFunctionPage = pathname.includes("/function");
  const isEnvironmentsPage = pathname.includes("/environments");
  const evaluationBasePath = agentId ? `/agents/${agentId}/evaluation` : "";
  const topLevelPathname = evaluationBasePath && pathname.startsWith(`${evaluationBasePath}/`) ? evaluationBasePath : pathname;
  const functionId = useSearchParam("f");
  const environmentId = useSearchParam("e");

  const isLoadingEnvironments = useAppSelector((state) => state.agentEnvironment.isLoading);
  const isLoadingFunction = useAppSelector((state) => state.agentFunction.isLoading || state.agentFunction.isLoadingCreate);
  const agentFunctionFormData = useAppSelector((state) => state.feature.agentFunctionFormData);
  const { organizationId: effectiveOrganizationId } = useEffectiveOrganization();
  const orgPhoneNumbers = useAppSelector((state) => Object.values(state.organizationPhoneNumbers.entities));
  const phoneNumbersByEnv = orgPhoneNumbers.reduce<Record<string, typeof orgPhoneNumbers>>((acc, pn) => {
    if (pn.agentEnvironmentId) {
      if (!acc[pn.agentEnvironmentId]) acc[pn.agentEnvironmentId] = [];
      acc[pn.agentEnvironmentId].push(pn);
    }
    return acc;
  }, {});
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [showDeleteFunctionConfirmDialog, setShowDeleteFunctionConfirmDialog] = useState(false);
  const [pendingDeleteFunctionId, setPendingDeleteFunctionId] = useState<string | null>(null);
  const [showDeleteEnvironmentConfirmDialog, setShowDeleteEnvironmentConfirmDialog] = useState(false);
  const [pendingDeleteEnvironmentId, setPendingDeleteEnvironmentId] = useState<string | null>(null);
  const [isDeletingEnvironment, setIsDeletingEnvironment] = useState(false);
  const [showNewVersionDialog, setShowNewVersionDialog] = useState(false);
  const [newVersionName, setNewVersionName] = useState("");
  const [versionNameError, setVersionNameError] = useState("");
  const [pendingFunctionId, setPendingFunctionId] = useState<string | undefined>();
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  const [nextLocation, setNextLocation] = useState<To | null>(null);
  const [isDeployingFunction, setIsDeployingFunction] = useState(false);
  const [deployLoadingMessageIndex, setDeployLoadingMessageIndex] = useState(0);
  const allowNavigationRef = useRef(false);
  const [showRunTestsDialog, setShowRunTestsDialog] = useState(false);
  const runTestsRequest = useApiRequest<{ data: { name: string; status: string; message: string; logs: string } }>();
  const agentDeploymentEntities = useAppSelector((state) => state.agentDeployment.entities);
  const prevShowRunTestsDialogRef = useRef(false);

  // Test notifications state
  const testNotifications = useAppSelector((state) => (state.testNotifications as any).notifications[agentId || ""] || []);

  // Fetch test notifications when agentId changes
  useEffect(() => {
    if (agentId) {
      dispatch(fetchTestNotifications(agentId));
    }
  }, [agentId, dispatch]);

  const [hasFetchedReleases, setHasFetchedReleases] = useState(false);
  useEffect(() => {
    if (!agentId) return;
    setHasFetchedReleases(false);
    const promise = dispatch(releaseActions.get({ agentId }));
    promise.finally(() => setHasFetchedReleases(true));
  }, [agentId, dispatch]);
  const showTabsSkeleton = !hasReleases && !hasFetchedReleases;

  const tabsSkeleton = (
    <View
      className="flex space-x-8 min-w-max items-center"
      style={{ height: 38 }}
    >
      {[64, 64, 72, 64, 80, 80, 64].map((w, i) => (
        <View
          key={i}
          className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
          style={{ width: w }}
        />
      ))}
    </View>
  );

  const onChangeFunction = (newFunctionId: string) => {
    const currentFunction = agentFunctions.find((func) => func.id === functionId);
    const isDirty = currentFunction && agentFunctionFormData.code !== currentFunction.code;
    if (isDirty) {
      setPendingFunctionId(newFunctionId);
      setShowConfirmDialog(true);
    } else {
      dispatch(featureActions.setAgentFunctionFormData(agentFunctions.find((func) => func.id === newFunctionId)));
      navigate(`/agents/${agentId}/function?f=${newFunctionId}`);
    }
  };

  const onChangeDetailsMenu = async (menuItem: string) => {
    if (menuItem === "delete") {
      setShowDeleteConfirmDialog(true);
    }
  };

  const onChangeEnvironmentMenu = async (menuItem: string) => {
    if (menuItem === "delete") {
      if (!environmentId) return;
      setPendingDeleteEnvironmentId(environmentId);
      setShowDeleteEnvironmentConfirmDialog(true);
    }
  };

  const handleCreateNewVersion = async () => {
    const trimmedName = newVersionName.trim();

    if (!trimmedName) {
      setVersionNameError("Version name is required");
      return;
    }

    const existingNames = agentFunctions.map((f) => (f.displayName || f.name).toLowerCase());
    if (existingNames.includes(trimmedName.toLowerCase())) {
      setVersionNameError("A version with this name already exists");
      return;
    }

    try {
      const createPayload = {
        agentId,
        code: agentFunctionFormData.code,
        language: agentFunctionFormData.language || "python",
        displayName: trimmedName,
        isMultifile: agentFunctionFormData.isMultifile,
      };

      const newFunction = await dispatch(agentFunctionActions.create(createPayload)).unwrap();

      // Refresh the function list to ensure latest data
      dispatch(agentFunctionActions.get({ agentId }));

      setShowNewVersionDialog(false);
      navigate(`/agents/${agentId}/function?f=${newFunction.id}`);
    } catch (error) {
      setVersionNameError("Failed to create version. Please try again.");
    }
  };

  const onChangeFunctionMenu = async (menuItem: string) => {
    if (menuItem === "new") {
      setShowNewVersionDialog(true);
      setNewVersionName("");
      setVersionNameError("");
    } else if (menuItem === "deploy") {
      setShowDeployDialog(true);
      if (agentEnvironments.length > 0) {
        setDeployEnvironmentId(agentEnvironments[0].id);
      }
    } else if (menuItem === "tests") {
      setShowRunTestsDialog(true);
    } else if (menuItem === "delete") {
      setPendingDeleteFunctionId(functionId);
      setShowDeleteFunctionConfirmDialog(true);
    }
  };

  const handleDeleteFunctionConfirm = async () => {
    if (!pendingDeleteFunctionId) return;
    await dispatch(agentFunctionActions.deleteById({ id: pendingDeleteFunctionId, agentId }));
    setShowDeleteFunctionConfirmDialog(false);
    setPendingDeleteFunctionId(null);
    navigate(`/agents/${agentId}/function`);
  };

  const handleDeleteEnvironmentConfirm = async () => {
    if (!pendingDeleteEnvironmentId || !agentId || isDeletingEnvironment) return;
    setIsDeletingEnvironment(true);
    try {
      await dispatch(agentEnvironmentActions.deleteById({ id: pendingDeleteEnvironmentId, agentId }));
      setShowDeleteEnvironmentConfirmDialog(false);
      setPendingDeleteEnvironmentId(null);
      navigate(`/agents/${agentId}/environments`);
    } finally {
      setIsDeletingEnvironment(false);
    }
  };

  const affectedEnvironmentsByFunctionDelete = agentEnvironments.filter(
    (env) => env.functionId === (pendingDeleteFunctionId || functionId) && (phoneNumbersByEnv[env.id]?.length ?? 0) > 0,
  );
  const environmentToDelete = pendingDeleteEnvironmentId ? agentEnvironments.find((env) => env.id === pendingDeleteEnvironmentId) : undefined;

  // Find active deployment for the current function
  const activeDeploymentForFunction = functionId
    ? Object.values(agentDeploymentEntities).find((deployment) => {
        const env = agentEnvironments.find((e) => e.id === deployment.environmentId);
        return deployment.agentId === agentId && deployment.isActive && env?.functionId === functionId;
      })
    : null;

  // Call run-tests API when dialog opens
  useEffect(() => {
    // Only call API when dialog transitions from closed to open
    if (showRunTestsDialog && !prevShowRunTestsDialogRef.current && agentId && functionId) {
      runTestsRequest.fetch(`/v1/agents/${agentId}/functions/${functionId}/run-tests`, {
        method: "POST",
      });
    }
    prevShowRunTestsDialogRef.current = showRunTestsDialog;
  }, [showRunTestsDialog, activeDeploymentForFunction?.id, agentId]);

  const onClickFunctionSave = async () => {
    dispatch(
      agentFunctionActions.updateById({
        id: functionId,
        agentId,
        code: agentFunctionFormData.code,
        language: agentFunctionFormData.language,
        name: agentFunctionFormData.name,
        isMultifile: agentFunctionFormData.isMultifile,
      }),
    );
  };

  const handleSaveAgentSettings = (updatedAgent: Partial<Agent>) => {
    if (agent) {
      dispatch(agentActions.updateById({ id: agentId, ...updatedAgent }));
    }
  };

  const onDeployFunction = async () => {
    if (!deployEnvironmentId || !functionId || isDeployingFunction) return;

    setIsDeployingFunction(true);
    try {
      await dispatch(
        agentEnvironmentActions.updateById({
          id: deployEnvironmentId,
          functionId,
          agentId,
          deploymentAction: "function",
          deploymentDedicated: deploymentIsDedicated,
        }),
      );
      setShowDeployDialog(false);
    } finally {
      setIsDeployingFunction(false);
    }
  };

  useEffect(() => {
    if (!isDeployingFunction) {
      setDeployLoadingMessageIndex(0);
      return;
    }

    const intervalId = window.setInterval(() => {
      setDeployLoadingMessageIndex((prev) => (prev + 1) % DEPLOY_LOADING_MESSAGES.length);
    }, 7500);

    return () => window.clearInterval(intervalId);
  }, [isDeployingFunction]);

  const onSaveShortcut = () => {
    if (isFunctionPage) {
      onClickFunctionSave();
    }
  };

  useKeyboardShortcut("CMD+S", onSaveShortcut);
  useKeyboardShortcut("CTRL+S", onSaveShortcut);

  useEffect(() => {
    if (agentId) {
      dispatch(agentActions.getById({ id: agentId }));
      dispatch(agentEnvironmentActions.get({ agentId }));
      dispatch(agentFunctionActions.get({ agentId }));
      // Fetch deployments to find active deployment for run tests
      dispatch(
        agentDeploymentActions.get({
          agentId,
          page: "1",
          limit: "100",
        }),
      );
    }
  }, [agentId, dispatch]); // Add dispatch to dependency array

  // Auto-switch organization context when viewing an agent from a different org
  useEffect(() => {
    if (agent && isAdmin && agent.organizationId !== effectiveOrganizationId) {
      window.location.href = "/";
    }
  }, [agent, isAdmin, effectiveOrganizationId, dispatch]);

  // Detect unsaved changes for function page
  const isFunctionPageDirty =
    isFunctionPage &&
    (() => {
      const currentFunction = agentFunctions.find((func) => func.id === functionId);
      return currentFunction && agentFunctionFormData.code !== currentFunction.code;
    })();

  // Intercept tab navigation
  const handleTabChange = (nextTab: string) => {
    if (isFunctionPageDirty && !allowNavigationRef.current) {
      setShowConfirmDialog(true);
      setPendingTab(nextTab);
    } else {
      allowNavigationRef.current = false;
      navigate(nextTab);
    }
  };

  // Block navigation if there are unsaved changes on the function page
  useBlocker((nextPath: To) => {
    if (allowNavigationRef.current) {
      allowNavigationRef.current = false;
      return false; // Allow navigation
    }
    if (isFunctionPageDirty) {
      setShowConfirmDialog(true);
      setNextLocation(nextPath);
      return true; // Block navigation
    }
    return false;
  }, isFunctionPageDirty);

  if (agentId && !agent) {
    if (isLoadingAgent) {
      return (
        <View className="min-h-screen bg-white dark:bg-slate-900 flex flex-col items-center justify-center">
          <Spinner size={32} />
        </View>
      );
    }
    return (
      <View className="min-h-screen bg-white dark:bg-slate-900 flex flex-col items-center justify-center gap-4">
        <Text className="text-lg font-semibold text-gray-900 dark:text-white">Agent not found</Text>
        <Button
          variant="info"
          size="sm"
          outline
          onClick={() => navigate("/agents")}
        >
          <Icon
            name="chevron-left"
            size={14}
          />
          Return to Agents
        </Button>
      </View>
    );
  }

  const handleConfirmCancel = () => {
    setShowConfirmDialog(false);
    setPendingFunctionId(undefined);
  };

  const handleDeleteConfirmCancel = () => {
    setShowDeleteConfirmDialog(false);
  };

  const handleDeleteConfirmOk = async () => {
    await dispatch(agentActions.deleteById({ id: agentId }));
    setShowDeleteConfirmDialog(false);
    navigate("/agents");
  };

  const handleConfirmDiscard = () => {
    setShowConfirmDialog(false);
    // Sync form data to current function so dirty state is cleared
    if (isFunctionPageDirty && functionId) {
      const currentFunction = agentFunctions.find((func) => func.id === functionId);
      if (currentFunction) {
        dispatch(featureActions.setAgentFunctionFormData(currentFunction));
      }
    }
    allowNavigationRef.current = true;
    if (pendingFunctionId) {
      dispatch(featureActions.setAgentFunctionFormData(agentFunctions.find((func) => func.id === pendingFunctionId)));
      navigate(`/agents/${agentId}/function?f=${pendingFunctionId}`);
      setPendingFunctionId(undefined);
    } else if (pendingTab) {
      navigate(pendingTab);
      setPendingTab(null);
    } else if (nextLocation) {
      navigate(nextLocation);
      setNextLocation(null);
    }
  };

  const handleConfirmSave = async () => {
    setShowConfirmDialog(false);
    allowNavigationRef.current = true;
    if (functionId) {
      await onClickFunctionSave();
    }
    if (pendingFunctionId) {
      dispatch(featureActions.setAgentFunctionFormData(agentFunctions.find((func) => func.id === pendingFunctionId)));
      navigate(`/agents/${agentId}/function?f=${pendingFunctionId}`);
      setPendingFunctionId(undefined);
    } else if (pendingTab) {
      navigate(pendingTab);
      setPendingTab(null);
    } else if (nextLocation) {
      navigate(nextLocation);
      setNextLocation(null);
    }
  };

  const agentTabs = [
    { id: `/agents/${agentId}/overview`, label: "Overview" },
    ...(uiVersion === "v1" ? [{ id: `/agents/${agentId}/environments`, label: "Environments" }] : []),
    { id: `/agents/${agentId}/function`, label: uiVersion === "v2" ? "Code" : "Function" },
    { id: `/agents/${agentId}/sessions`, label: "Sessions" },
    ...(uiVersion === "v1" ? [{ id: `/agents/${agentId}/deployments`, label: "Deployments" }] : []),
    ...(hasReleases ? [{ id: `/agents/${agentId}/releases`, label: "Releases" }] : []),
    { id: `/agents/${agentId}/evaluation`, label: "Evaluations" },
    { id: `/agents/${agentId}/experiments`, label: "Experiments" },
    { id: `/agents/${agentId}/metrics`, label: "Metrics" },
  ];

  return (
    <View className="relative h-full overflow-hidden flex flex-col bg-white dark:bg-slate-900">
      {/* Header - always full width */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="border-b border-gray-200 dark:border-gray-800 flex-shrink-0"
      >
        <View className="w-full">
          {/* Agent Header Section with Tabs */}
          {agent && (
            <View
              className="px-5 py-3"
              data-onboarding="agent-status"
            >
              {/* Desktop Layout - Agent name, edit button, and tabs on same line */}
              <View className="hidden lg:block">
                {/* Single row: Agent name, edit button, tabs, version picker, action buttons */}
                <View className="flex items-center gap-2">
                  {/* Left side: Agent name, edit button, and tabs */}
                  <View className="flex items-center gap-4 min-w-0 flex-1">
                    {/* Agent name and edit button */}
                    <View className="flex items-center gap-0">
                      <Text className="text-xl font-bold text-gray-900 dark:text-white whitespace-nowrap max-w-[500px] truncate">
                        {agent.name || "Unnamed Agent"}
                      </Text>

                      <IconButton
                        variant="transparent"
                        icon="edit"
                        onClick={() => setShowAgentSettingsModal(true)}
                      />
                    </View>

                    <View className="relative overflow-x-auto flex-1 no-scrollbar">
                      {showTabsSkeleton ? (
                        tabsSkeleton
                      ) : (
                        <Tabs
                          tabs={agentTabs}
                          activeTab={topLevelPathname}
                          onChange={handleTabChange}
                          className="min-w-max"
                        />
                      )}
                      {testNotifications.length > 0 && <View className="absolute top-3 right-0 w-2 h-2 bg-yellow-500 rounded-full" />}
                    </View>

                    {/* Version dropdown - show only on function page, always outside tabs container */}
                    {isFunctionPage && functionId && (
                      <DropdownField
                        className="w-36 min-w-36 max-w-36 flex-shrink-0"
                        placeholder="Version"
                        onChange={onChangeFunction}
                        value={functionId}
                        options={agentFunctions
                          ?.map((version) => {
                            const environmentsUsingVersion = agentEnvironments.filter((env) => env.functionId === version.id);
                            const displayName = version.id === "new" ? "New Version" : version.displayName || version.name;
                            return {
                              label: (
                                <View className="flex items-center gap-2">
                                  {environmentsUsingVersion.length > 0 && (
                                    <View className="flex items-center gap-1">
                                      {environmentsUsingVersion.map((env) => {
                                        const gradient = getGradientForName(env.name);
                                        return (
                                          <View
                                            key={env.id}
                                            className={`h-5 w-5 rounded-full bg-gradient-to-br ${gradient.from} ${gradient.to} ${gradient.darkFrom} ${gradient.darkTo} flex items-center justify-center`}
                                            title={env.name}
                                          >
                                            <Text className="text-xs font-bold text-white">{env.name.substring(0, 1).toUpperCase()}</Text>
                                          </View>
                                        );
                                      })}
                                    </View>
                                  )}
                                  <div className="flex flex-col">
                                    <Text className="text-sm line-clamp-2">{displayName}</Text>
                                    <Text className="text-xs line-clamp-2 !text-gray-500 dark:!text-gray-400">{version.name}</Text>
                                  </div>
                                </View>
                              ),
                              displayLabel: <Text className="text-sm line-clamp-2">{displayName}</Text>,
                              searchValue: displayName,
                              value: version.id,
                              sortOrder: environmentsUsingVersion.length > 0 ? 0 : 1,
                            };
                          })
                          .sort((a, b) => a.sortOrder - b.sortOrder)}
                      />
                    )}
                  </View>

                  {/* Right side: Action buttons */}
                  <View className="flex items-center justify-end flex-shrink-0 gap-2 ml-auto">
                    {!isFunctionPage && (
                      <>
                        {isEnvironmentsPage && agentEnvironments.length > 0 && !environmentId && (
                          <Button
                            variant="info"
                            size="sm"
                            outline
                            onClick={() => setShowCreateDialog(true)}
                            disabled={isLoadingEnvironments}
                          >
                            <Icon
                              name="add"
                              size={14}
                            />
                            {isLoadingEnvironments ? "Loading..." : "Environment"}
                          </Button>
                        )}
                        {isEnvironmentsPage && environmentId && (
                          <MenuField
                            onChange={onChangeEnvironmentMenu}
                            value={environmentId}
                            options={[{ label: "Delete Environment", value: "delete", textColor: "text-red-500" }]}
                          />
                        )}
                      </>
                    )}
                    {isFunctionPage && functionId && (
                      <>
                        <Button
                          variant="info"
                          size="sm"
                          outline
                          onClick={onClickFunctionSave}
                          disabled={isLoadingFunction}
                        >
                          <Icon
                            name="save"
                            size={14}
                          />
                          {isLoadingFunction ? "Saving..." : "Save"}
                        </Button>
                        <MenuField
                          onChange={onChangeFunctionMenu}
                          value={functionId}
                          options={[
                            { label: "New Version", value: "new" },
                            { label: "Deploy Version", value: "deploy" },
                            { label: "Run Tests", value: "tests" },
                            { label: "Delete Version", value: "delete", textColor: "text-red-500" },
                          ]}
                        />
                      </>
                    )}
                  </View>
                </View>
              </View>

              {/* Mobile Layout - Agent name and edit button on top, tabs below */}
              <View className="lg:hidden space-y-3">
                {/* Mobile Agent name and edit button */}
                <View className="flex items-center justify-between">
                  <View className="flex items-center gap-2">
                    <span className="text-lg font-bold text-gray-900 dark:text-white">{agent.name || "Unnamed Agent"}</span>
                    <button
                      onClick={() => setShowAgentSettingsModal(true)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
                    >
                      <Icon
                        name="edit"
                        size={14}
                        className="text-gray-400 dark:text-gray-500"
                      />
                    </button>
                  </View>
                </View>

                {/* Mobile Tabs */}
                <View className="relative overflow-x-auto no-scrollbar">
                  {showTabsSkeleton ? (
                    tabsSkeleton
                  ) : (
                    <Tabs
                      tabs={agentTabs}
                      activeTab={topLevelPathname}
                      onChange={handleTabChange}
                      className="min-w-max"
                    />
                  )}
                  {testNotifications.length > 0 && <View className="absolute top-3 right-0 w-2 h-2 bg-yellow-500 rounded-full" />}
                </View>
              </View>
            </View>
          )}
        </View>
      </motion.div>

      {/* Content area + Debugger */}
      <View className={clsx("flex-1 overflow-hidden", isFunctionPage ? "grid grid-cols-[1fr_auto]" : "flex flex-row")}>
        <motion.div
          initial="hidden"
          animate="visible"
          className="flex flex-col flex-1 h-full overflow-hidden pb-20 lg:pb-0"
          variants={itemVariants}
        >
          <Outlet />
        </motion.div>
        {isFunctionPage && (
          <div
            data-onboarding="try-it-button"
            className="h-full overflow-hidden"
          >
            {hasReleases ? (
              <ReleaseDebugger
                agentId={agentId || ""}
                isOpen={isDebuggerOpen}
                onClick={() => setIsDebuggerOpen(!isDebuggerOpen)}
              />
            ) : (
              <AgentDebugger
                agentId={agentId || ""}
                functionId={functionId || ""}
                isOpen={isDebuggerOpen}
                onClick={() => setIsDebuggerOpen(!isDebuggerOpen)}
              />
            )}
          </div>
        )}
      </View>
      {isEnvironmentsPage && (
        <CreateAgentEnvironmentDialog
          agentId={agentId || ""}
          isOpen={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
        />
      )}
      <Dialog
        isOpen={showDeployDialog}
        onClose={() => (isDeployingFunction ? null : setShowDeployDialog(false))}
        title="Deploy Function"
        actions={
          <>
            <Button
              variant="neutral"
              onClick={() => (isDeployingFunction ? null : setShowDeployDialog(false))}
              isDisabled={isDeployingFunction}
            >
              Cancel
            </Button>
            <Button
              onClick={onDeployFunction}
              isDisabled={!deployEnvironmentId || isDeployingFunction}
              className="brand"
            >
              <View className="flex flex-row items-center gap-2">
                {isDeployingFunction ? (
                  <Spinner
                    size={16}
                    variant="brand"
                    className="text-white"
                  />
                ) : (
                  <Icon
                    name="rocket"
                    size={16}
                  />
                )}
                <Text className="text-white">{isDeployingFunction ? "Deploying…" : "Deploy"}</Text>
              </View>
            </Button>
          </>
        }
      >
        <View className="flex flex-col gap-4 p-4">
          {isDeployingFunction && (
            <View className="flex flex-row items-start gap-3 rounded-lg border border-brand/20 dark:border-brand-light/20 bg-brand/5 dark:bg-brand-light/10 p-4">
              <View className="mt-0.5">
                <Spinner
                  size={22}
                  variant="brand"
                />
              </View>
              <View className="flex flex-col gap-1 flex-1">
                <View className="flex flex-row items-center gap-2">
                  <Icon
                    name="sparkles"
                    size={18}
                    className="text-brand dark:text-brand-light"
                  />
                  <Text className="font-semibold text-gray-900 dark:text-gray-100">Deploying… this could take a minute</Text>
                </View>
                <Text className="text-sm text-gray-600 dark:text-gray-300">{DEPLOY_LOADING_MESSAGES[deployLoadingMessageIndex]}</Text>
              </View>
            </View>
          )}
          <Text>Select an environment to deploy this function version to:</Text>
          <AgentEnvironmentDropdownField
            agentEnvironments={agentEnvironments}
            value={deployEnvironmentId}
            onChange={setDeployEnvironmentId}
            readOnly={isDeployingFunction}
          />
        </View>
        {user?.isAdmin && (
          <View className="flex flex-col gap-4 p-4">
            <SwitchField
              label="Dedicated deployment"
              tooltip="A dedicated deployment uses its own resources and is not shared with other deployments/environments."
              options={[
                { label: "No", value: "no" },
                { label: "Yes", value: "yes" },
              ]}
              value={deploymentIsDedicated ? "yes" : "no"}
              onChange={(value: string | null) => setDeploymentIsDedicated(value === "yes")}
              isDisabled={isDeployingFunction}
            />
          </View>
        )}
      </Dialog>
      <Dialog
        isOpen={showNewVersionDialog}
        onClose={() => setShowNewVersionDialog(false)}
        title="Create New Version"
      >
        <View className="flex flex-col gap-4 p-4">
          <Text>Give this version a unique name to identify it:</Text>
          <InputField
            label="Version Name"
            placeholder="e.g. v2"
            value={newVersionName}
            onChange={(value: string) => {
              setNewVersionName(value);
              if (versionNameError) setVersionNameError("");
            }}
            error={versionNameError}
          />

          <View className="flex justify-end space-x-3 pt-4">
            <Button
              variant="neutral"
              onClick={() => setShowNewVersionDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateNewVersion}
              isDisabled={!newVersionName.trim()}
              variant="brand"
            >
              Create Version
            </Button>
          </View>
        </View>
      </Dialog>
      {showConfirmDialog && (
        <Dialog
          isOpen={showConfirmDialog}
          onClose={handleConfirmCancel}
        >
          <View className="p-4">
            <View>
              <Text className="text-lg font-semibold mb-4">Unsaved Changes</Text>
              <View>
                <Text className="mb-6">You have unsaved changes in the current function. What would you like to do?</Text>
              </View>
            </View>
            <View className="flex justify-end gap-4">
              <Button
                onClick={handleConfirmCancel}
                variant="ghost"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDiscard}
                variant="neutral"
              >
                Discard
              </Button>
              <Button
                onClick={handleConfirmSave}
                variant="brand"
              >
                Save
              </Button>
            </View>
          </View>
        </Dialog>
      )}
      {showDeleteConfirmDialog && (
        <Dialog
          isOpen={showDeleteConfirmDialog}
          onClose={handleDeleteConfirmCancel}
          title="Delete Agent"
          actions={
            <>
              <Button
                variant="neutral"
                onClick={handleDeleteConfirmCancel}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirmOk}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                Delete
              </Button>
            </>
          }
        >
          <View className="p-4">
            <Text>Are you sure you want to delete this agent? This action cannot be undone.</Text>
          </View>
        </Dialog>
      )}
      {showDeleteFunctionConfirmDialog && (
        <Dialog
          isOpen={showDeleteFunctionConfirmDialog}
          onClose={() => setShowDeleteFunctionConfirmDialog(false)}
          title="Delete Function Version"
          actions={
            <>
              <Button
                variant="neutral"
                onClick={() => setShowDeleteFunctionConfirmDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteFunctionConfirm}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {affectedEnvironmentsByFunctionDelete.length > 0 ? "Delete Anyway" : "Delete"}
              </Button>
            </>
          }
        >
          <View className="p-4 space-y-3">
            {affectedEnvironmentsByFunctionDelete.length > 0 ? (
              <>
                <View className="flex items-start gap-3 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                  <Icon
                    name="warning-amber"
                    size={20}
                    className="text-warning-dark flex-shrink-0 mt-0.5"
                  />
                  <View>
                    <Text className="font-medium text-warning-dark mr-1">Calls will fail after deletion</Text>
                    <Text className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      This function is deployed to {affectedEnvironmentsByFunctionDelete.length === 1 ? "an environment" : "environments"} with{" "}
                      {affectedEnvironmentsByFunctionDelete.length === 1 ? "a phone number" : "phone numbers"}:
                    </Text>
                  </View>
                </View>
                <View className="space-y-2 pl-2">
                  {affectedEnvironmentsByFunctionDelete.map((env) => (
                    <View
                      key={env.id}
                      className="flex items-center gap-2"
                    >
                      <View
                        className={`h-6 w-6 rounded-full bg-gradient-to-br ${getGradientForName(env.name).from} ${getGradientForName(env.name).to} flex items-center justify-center`}
                      >
                        <Text className="text-xs font-bold text-white">{env.name.substring(0, 1).toUpperCase()}</Text>
                      </View>
                      <Text className="text-sm font-medium">{env.name}</Text>
                      <Text className="text-sm text-gray-500">{phoneNumbersByEnv[env.id]?.map((pn) => formatPhoneNumber(pn.phoneNumber)).join(", ")}</Text>
                    </View>
                  ))}
                </View>
              </>
            ) : (
              <Text>Are you sure you want to delete this function version? This action cannot be undone.</Text>
            )}
          </View>
        </Dialog>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirmDialog}
        onClose={handleDeleteConfirmCancel}
        onConfirm={handleDeleteConfirmOk}
        title="Delete Agent"
        message="Are you sure you want to delete this agent? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
      />
      <ConfirmDialog
        isOpen={showDeleteEnvironmentConfirmDialog}
        onClose={() => {
          if (isDeletingEnvironment) return;
          setShowDeleteEnvironmentConfirmDialog(false);
          setPendingDeleteEnvironmentId(null);
        }}
        onConfirm={handleDeleteEnvironmentConfirm}
        title="Delete Environment"
        message={
          environmentToDelete?.name
            ? `Are you sure you want to delete the environment "${environmentToDelete.name}"? This action cannot be undone.`
            : "Are you sure you want to delete this environment? This action cannot be undone."
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isDeletingEnvironment}
        variant="danger"
      />
      <ConfirmDialog
        isOpen={showDeleteFunctionConfirmDialog}
        onClose={() => setShowDeleteFunctionConfirmDialog(false)}
        onConfirm={handleDeleteFunctionConfirm}
        title="Delete Function Version"
        message={
          affectedEnvironmentsByFunctionDelete.length > 0
            ? "Deleting this function version will cause calls to fail for the environments below."
            : "Are you sure you want to delete this function version? This action cannot be undone."
        }
        confirmLabel={affectedEnvironmentsByFunctionDelete.length > 0 ? "Delete Anyway" : "Delete"}
        cancelLabel="Cancel"
        variant={affectedEnvironmentsByFunctionDelete.length > 0 ? "warning" : "danger"}
      >
        {affectedEnvironmentsByFunctionDelete.length > 0 ? (
          <View className="space-y-3">
            <View className="flex items-start gap-3 p-3 bg-warning/10 border border-warning/20 rounded-lg">
              <Icon
                name="warning-amber"
                size={20}
                className="text-warning-dark flex-shrink-0 mt-0.5"
              />
              <View>
                <Text className="font-medium text-warning-dark mr-1">Calls will fail after deletion</Text>
                <Text className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  This function is deployed to {affectedEnvironmentsByFunctionDelete.length === 1 ? "an environment" : "environments"} with{" "}
                  {affectedEnvironmentsByFunctionDelete.length === 1 ? "a phone number" : "phone numbers"}:
                </Text>
              </View>
            </View>
            <View className="space-y-2 pl-2">
              {affectedEnvironmentsByFunctionDelete.map((env) => (
                <View
                  key={env.id}
                  className="flex items-center gap-2"
                >
                  <View
                    className={`h-6 w-6 rounded-full bg-gradient-to-br ${getGradientForName(env.name).from} ${getGradientForName(env.name).to} flex items-center justify-center`}
                  >
                    <Text className="text-xs font-bold text-white">{env.name.substring(0, 1).toUpperCase()}</Text>
                  </View>
                  <Text className="text-sm font-medium">{env.name}</Text>
                  <Text className="text-sm text-gray-500">{phoneNumbersByEnv[env.id]?.map((pn) => formatPhoneNumber(pn.phoneNumber)).join(", ")}</Text>
                </View>
              ))}
            </View>
          </View>
        ) : null}
      </ConfirmDialog>

      {/* Run Tests Dialog */}
      <Dialog
        title="Run Tests"
        className="w-full h-full max-w-6xl max-h-[90vh]"
        size="flex"
        isOpen={showRunTestsDialog}
        onClose={() => setShowRunTestsDialog(false)}
      >
        <View className="flex flex-col flex-1 gap-4 p-4">
          {!activeDeploymentForFunction ? (
            <View className="flex flex-col gap-2">
              <Text className="text-gray-900 dark:text-white">No active deployment found for this function.</Text>
              <Text className="text-sm text-gray-600 dark:text-gray-400">Please deploy this function to an environment before running tests.</Text>
            </View>
          ) : runTestsRequest.isLoading ? (
            <View className="flex flex-row items-start gap-3 rounded-lg border border-brand/20 dark:border-brand-light/20 bg-brand/5 dark:bg-brand-light/10 p-4">
              <View className="mt-0.5">
                <Spinner
                  size={22}
                  variant="brand"
                />
              </View>
              <View className="flex flex-col gap-1 flex-1">
                <Text className="font-semibold text-gray-900 dark:text-gray-100">Running tests…</Text>
                <Text className="text-sm text-gray-600 dark:text-gray-300">Please wait while tests are executed.</Text>
              </View>
            </View>
          ) : runTestsRequest.error ? (
            <Alert variant="destructive">
              <AlertTitle>Error running tests</AlertTitle>
              <AlertDescription>{String(runTestsRequest.error)}</AlertDescription>
            </Alert>
          ) : runTestsRequest.data ? (
            <View className="flex flex-col flex-1 gap-4">
              <Alert variant={runTestsRequest.data.data.status === "succeeded" ? "success" : "destructive"}>
                <AlertTitle>{runTestsRequest.data.data.name || "Test Results"}</AlertTitle>
                {runTestsRequest.data.data.message && <AlertDescription>{runTestsRequest.data.data.message}</AlertDescription>}
                {runTestsRequest.data.data.status && (
                  <View className="flex flex-row items-center gap-2 mt-1">
                    <Text className="text-xs font-medium text-gray-600 dark:text-gray-400">Status:</Text>
                    <Text className="text-xs font-semibold">{capitalize(runTestsRequest.data.data.status)}</Text>
                  </View>
                )}
              </Alert>
              {runTestsRequest.data.data.logs && (
                <View className="flex flex-col flex-1 gap-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 p-4">
                  <Text className="text-sm font-semibold text-gray-900 dark:text-gray-100">Logs</Text>
                  <View className="flex-1 rounded-md bg-gray-900 dark:bg-gray-950 p-3 overflow-auto max-h-96">
                    <Text className="text-xs font-mono text-gray-100 whitespace-pre">{runTestsRequest.data.data.logs}</Text>
                  </View>
                </View>
              )}
            </View>
          ) : null}
        </View>
      </Dialog>

      {/* Agent Settings Modal */}
      <AgentSettingsModal
        agent={agent}
        isOpen={showAgentSettingsModal}
        onClose={() => setShowAgentSettingsModal(false)}
        onSave={handleSaveAgentSettings}
        onMenuAction={onChangeDetailsMenu}
      />

      {/* Mobile sticky save button */}
      {isFunctionPage && functionId && (
        <View className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-gray-800 p-4 z-40">
          <Button
            variant="info"
            size="sm"
            outline
            onClick={onClickFunctionSave}
            disabled={isLoadingFunction}
            className="justify-center w-full"
          >
            <Icon
              name="save"
              size={14}
            />
            {isLoadingFunction ? "Saving..." : "Save Function"}
          </Button>
        </View>
      )}
    </View>
  );
};
